// CalendarPage — главная страница календаря: виды Месяц/Неделя/День (M3, U-39),
// date range filter, CRM-фильтры (компания/контакт/живой поиск, R21.1),
// FAB "Создать событие", CSV-экспорт событий текущего диапазона (R22, U6-export).
// State навигации и диапазона — useCalendarView (M1 локальные границы, M3 видозависимый title).
import { useState, useMemo, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Stack,
  Fab,
  Paper,
  Button,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { EmptyState } from '@/theme/EmptyState';
import { MonthGrid } from './components/MonthGrid';
import { TimeGridWeek } from './components/TimeGridWeek';
import { TimeGridDay } from './components/TimeGridDay';
import { CalendarViewToggle } from './components/CalendarViewToggle';
import { DateRangeFilter, type DateRangeValue } from './components/DateRangeFilter';
import { CrmEventFilters, type CrmEventFiltersValue } from './components/CrmEventFilters';
import { useCalendarView } from './hooks/useCalendarView';
import { calendarApi, type CalendarEvent } from '@/api/calendar';
import {
  loadDealMapForExport,
  loadCompanyMapForExport,
  loadContactMapForExport,
  resolveNameOrDash,
} from '@/lib/exportResolve';
import { formatDate } from '@/lib/format';
import { toCsv, downloadCsv, type CsvRow } from '@/lib/csv';

/** Задержка debounce для живого поиска (мс). */
const SEARCH_DEBOUNCE_MS = 300;

/** Размер страницы для CSV-экспорта: полная выборка одним запросом (KISS). */
const EXPORT_PAGE_SIZE = 1000;

/** Заголовки колонок CSV-экспорта событий календаря. */
const EVENTS_CSV_HEADERS = [
  'Название',
  'Начало',
  'Конец',
  'Весь день',
  'Место',
  'Сделка',
  'Контакт',
  'Компания',
];

/** Дефолтное (пустое) значение CRM-фильтров. */
const DEFAULT_CRM_FILTERS: CrmEventFiltersValue = { companyId: null, contactId: null, search: '' };

/** Возвращает значение с задержкой debounce (для живого поиска без спама запросами). */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Главная страница календаря. */
export function CalendarPage(): JSX.Element {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRangeValue>({ from: '', to: '' });
  const [crmFilters, setCrmFilters] = useState<CrmEventFiltersValue>(DEFAULT_CRM_FILTERS);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(crmFilters.search, SEARCH_DEBOUNCE_MS);
  // M3: anchorDate (единственный источник времени) + viewMode + производные (month/range/title).
  const { viewMode, setViewMode, month, range, title, prev, next, today } = useCalendarView(dateRange);

  // Запрос событий за видимый диапазон (M1: локальные границы — из useCalendarView).
  const queryRange = useMemo(() => ({ from: range.from, to: range.to }), [range.from, range.to]);

  const query = useQuery({
    queryKey: ['calendar-events', queryRange, crmFilters.companyId, crmFilters.contactId, debouncedSearch],
    queryFn: () =>
      calendarApi.list({
        from: queryRange.from,
        to: queryRange.to,
        size: 1000,
        ...(crmFilters.companyId ? { companyId: crmFilters.companyId } : {}),
        ...(crmFilters.contactId ? { contactId: crmFilters.contactId } : {}),
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
      }),
  });

  // События видимого диапазона и переход к карточке — общие для всех видов (M3).
  const events = query.data?.content ?? [];
  const handleEventClick = (event: CalendarEvent): void => {
    navigate(`/calendar/events/${event.id}`);
  };
  const handleDayClick = (day: Date): void => {
    navigate(`/calendar/day/${format(day, 'yyyy-MM-dd')}`);
  };

  // FIX-C (U-15): названия сделок/компаний/контактов для CSV-экспорта — полный резолв
  // по экспортной выборке (loadDealMapForExport — поштучные GET, справочники —
  // постраничный сбор), а не по видимой странице (size=200), где dealMap строился
  // repeat-queries'ами и не покрывал события экспорта (раньше в CSV утекали UUID,
  // а сделки вне видимой страницы показывались «—»).

  /** Экспорт CSV (R22): события текущего диапазона + активные CRM-фильтры (size=1000). */
  const handleExportCsv = async (): Promise<void> => {
    setExporting(true);
    setExportError(null);
    try {
      const data = await calendarApi.list({
        from: queryRange.from,
        to: queryRange.to,
        size: EXPORT_PAGE_SIZE,
        ...(crmFilters.companyId ? { companyId: crmFilters.companyId } : {}),
        ...(crmFilters.contactId ? { contactId: crmFilters.contactId } : {}),
        ...(debouncedSearch ? { q: debouncedSearch } : {}),
      });
      const [exportDealMap, exportCompanyMap, exportContactMap] = await Promise.all([
        loadDealMapForExport(data.content.map((ev) => ev.dealId)),
        loadCompanyMapForExport(data.content.map((ev) => ev.companyId)),
        loadContactMapForExport(data.content.map((ev) => ev.contactId)),
      ]);
      const rows: CsvRow[] = data.content.map((ev) => [
        ev.title,
        formatDate(ev.startAt),
        formatDate(ev.endAt),
        ev.allDay ? 'да' : 'нет',
        ev.location ?? '',
        resolveNameOrDash(exportDealMap, ev.dealId),
        resolveNameOrDash(exportContactMap, ev.contactId),
        resolveNameOrDash(exportCompanyMap, ev.companyId),
      ]);
      downloadCsv(`events_${format(new Date(), 'yyyy-MM-dd')}.csv`, toCsv(rows, EVENTS_CSV_HEADERS));
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Неизвестная ошибка');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title={title}
        subtitle="Календарь событий"
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
              disabled={exporting}
              aria-label="Экспорт CSV"
            >
              Экспорт CSV
            </Button>
            <Button startIcon={<ChevronLeftIcon />} onClick={prev}>
              Назад
            </Button>
            <Button onClick={today}>Сегодня</Button>
            <Button endIcon={<ChevronRightIcon />} onClick={next}>
              Вперёд
            </Button>
          </Stack>
        }
      />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mb: 1 }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
      >
        <CalendarViewToggle value={viewMode} onChange={setViewMode} />
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
      </Stack>
      <CrmEventFilters
        value={crmFilters}
        onChange={setCrmFilters}
        onReset={() => setCrmFilters(DEFAULT_CRM_FILTERS)}
      />
      {exportError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Не удалось экспортировать CSV: {exportError}
        </Alert>
      ) : null}
      {query.isLoading ? (
        <LoadingSpinner label="Загрузка событий…" />
      ) : query.isError ? (
        <EmptyState
          title="Не удалось загрузить события"
          description={query.error instanceof Error ? query.error.message : 'Неизвестная ошибка'}
        />
      ) : viewMode === 'month' ? (
        <Paper sx={{ p: 2 }}>
          <MonthGrid month={month} events={events} onDayClick={handleDayClick} onEventClick={handleEventClick} />
        </Paper>
      ) : viewMode === 'week' ? (
        <Paper sx={{ p: 2 }}>
          <TimeGridWeek days={range.days} events={events} onEventClick={handleEventClick} />
        </Paper>
      ) : (
        <Paper sx={{ p: 2 }}>
          <TimeGridDay day={range.days[0] as Date} events={events} onEventClick={handleEventClick} />
        </Paper>
      )}
      <Stack direction="row" justifyContent="flex-end">
        <Fab
          component={RouterLink}
          to="/calendar/new"
          color="primary"
          aria-label="Создать событие"
          sx={{ position: 'fixed', bottom: 24, right: 24 }}
        >
          <AddIcon />
        </Fab>
      </Stack>
    </Box>
  );
}
